import { useState, useEffect } from "react";
import { Layers, Plus, RefreshCw, XCircle, RotateCcw, Trash2, ChevronDown } from "lucide-react";
import { BACKEND_URL } from "../../context/AuthContext";

const STATUS_COLORS = {
  pending: 'bg-yellow-500/20 text-yellow-400',
  running: 'bg-blue-500/20 text-blue-400',
  completed: 'bg-green-500/20 text-green-400',
  failed: 'bg-red-500/20 text-red-400',
  cancelled: 'bg-gray-500/20 text-gray-400',
};

const JOB_TYPE_LABELS = {
  podcast_generation: 'Podcast Generation',
  dj_generation: 'DJ Voice Generation',
  voice_synthesis: 'Voice Synthesis',
  artwork_creation: 'Artwork Creation',
  show_notes: 'Show Notes',
  transcript: 'Transcript',
  music_analysis: 'Music Analysis',
  content_scheduling: 'Content Scheduling',
  promo_generation: 'Promo Generation',
  custom: 'Custom',
};

export const JobQueue = ({ authToken }) => {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newJob, setNewJob] = useState({ job_type: 'podcast_generation', title: '', description: '', provider: 'openai' });

  useEffect(() => { fetchJobs(); }, [filter]);

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const res = await fetch(`${BACKEND_URL}/api/ai/jobs${params}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.ok) {
        const data = await res.json();
        setJobs(data.jobs || []);
        setStats(data.stats || {});
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const createJob = async () => {
    if (!newJob.title.trim()) return;
    setCreating(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ai/jobs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(newJob),
      });
      if (res.ok) {
        setShowCreate(false);
        setNewJob({ job_type: 'podcast_generation', title: '', description: '', provider: 'openai' });
        fetchJobs();
      }
    } catch (err) { console.error(err); }
    finally { setCreating(false); }
  };

  const cancelJob = async (jobId) => {
    try {
      await fetch(`${BACKEND_URL}/api/ai/jobs/${jobId}/cancel`, {
        method: 'PUT', headers: { Authorization: `Bearer ${authToken}` },
      });
      fetchJobs();
    } catch (err) { console.error(err); }
  };

  const retryJob = async (jobId) => {
    try {
      await fetch(`${BACKEND_URL}/api/ai/jobs/${jobId}/retry`, {
        method: 'PUT', headers: { Authorization: `Bearer ${authToken}` },
      });
      fetchJobs();
    } catch (err) { console.error(err); }
  };

  const deleteJob = async (jobId) => {
    try {
      await fetch(`${BACKEND_URL}/api/ai/jobs/${jobId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` },
      });
      fetchJobs();
    } catch (err) { console.error(err); }
  };

  return (
    <div data-testid="ai-job-queue" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Layers className="w-5 h-5 text-cyan-400" /> Job Queue
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={fetchJobs} className="text-white/40 hover:text-white transition p-2" data-testid="ai-jobs-refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
          <button onClick={() => setShowCreate(!showCreate)}
            className="flex items-center gap-1.5 bg-cyan-600 hover:bg-cyan-500 text-white px-3 py-1.5 rounded-lg text-xs font-medium transition"
            data-testid="ai-jobs-create-btn">
            <Plus className="w-3.5 h-3.5" /> New Job
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-5 gap-2">
        {[
          { key: 'all', label: 'Total', value: stats.total || 0 },
          { key: 'pending', label: 'Pending', value: stats.pending || 0 },
          { key: 'running', label: 'Running', value: stats.running || 0 },
          { key: 'completed', label: 'Done', value: stats.completed || 0 },
          { key: 'failed', label: 'Failed', value: stats.failed || 0 },
        ].map(s => (
          <button key={s.key} onClick={() => setFilter(s.key)}
            className={`p-3 rounded-xl text-center transition text-xs ${filter === s.key ? 'bg-cyan-500/15 border border-cyan-500/30' : 'bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06]'}`}
            data-testid={`ai-jobs-filter-${s.key}`}>
            <p className="text-lg font-bold text-white">{s.value}</p>
            <p className="text-white/40">{s.label}</p>
          </button>
        ))}
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="bg-white/[0.03] border border-cyan-500/20 rounded-xl p-5 space-y-3" data-testid="ai-jobs-create-form">
          <h4 className="text-white font-semibold text-sm">Create AI Job</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-white/50 text-xs mb-1">Job Type</label>
              <select value={newJob.job_type} onChange={e => setNewJob(p => ({ ...p, job_type: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
                data-testid="ai-jobs-type-select">
                {Object.entries(JOB_TYPE_LABELS).map(([k, v]) => (
                  <option key={k} value={k} className="bg-gray-900">{v}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-white/50 text-xs mb-1">Provider</label>
              <select value={newJob.provider} onChange={e => setNewJob(p => ({ ...p, provider: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
                data-testid="ai-jobs-provider-select">
                <option value="openai" className="bg-gray-900">OpenAI</option>
                <option value="gemini" className="bg-gray-900">Gemini</option>
                <option value="anthropic" className="bg-gray-900">Anthropic</option>
                <option value="local" className="bg-gray-900">Local</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-white/50 text-xs mb-1">Title</label>
            <input type="text" value={newJob.title} onChange={e => setNewJob(p => ({ ...p, title: e.target.value }))}
              placeholder="My AI Job..." className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none"
              data-testid="ai-jobs-title-input" />
          </div>
          <div>
            <label className="block text-white/50 text-xs mb-1">Description</label>
            <textarea value={newJob.description} onChange={e => setNewJob(p => ({ ...p, description: e.target.value }))}
              placeholder="Optional description..." rows={2}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none resize-none"
              data-testid="ai-jobs-desc-input" />
          </div>
          <div className="flex gap-2">
            <button onClick={createJob} disabled={creating || !newJob.title.trim()}
              className="bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
              data-testid="ai-jobs-submit">
              {creating ? 'Creating...' : 'Queue Job'}
            </button>
            <button onClick={() => setShowCreate(false)} className="bg-white/5 hover:bg-white/10 text-white/60 px-4 py-2 rounded-lg text-sm transition">Cancel</button>
          </div>
        </div>
      )}

      {/* Jobs List */}
      {loading ? (
        <div className="text-center py-8"><div className="w-6 h-6 border-2 border-white/20 border-t-cyan-500 rounded-full animate-spin mx-auto" /></div>
      ) : jobs.length === 0 ? (
        <div className="text-center py-12 bg-white/[0.02] border border-white/[0.06] rounded-2xl">
          <Layers className="w-10 h-10 text-white/10 mx-auto mb-3" />
          <p className="text-white/30 text-sm">No jobs {filter !== 'all' ? `with status "${filter}"` : 'yet'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {jobs.map(job => (
            <div key={job.job_id} className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-4" data-testid={`ai-job-${job.job_id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${STATUS_COLORS[job.status] || 'bg-white/10 text-white/50'}`}>
                      {job.status}
                    </span>
                    <span className="text-white font-medium text-sm truncate">{job.title}</span>
                  </div>
                  <div className="flex items-center gap-3 text-[11px] text-white/30">
                    <span className="text-cyan-400/60">{JOB_TYPE_LABELS[job.job_type] || job.job_type}</span>
                    <span>{job.provider}</span>
                    <span>{new Date(job.created_at).toLocaleString()}</span>
                    {job.retry_count > 0 && <span className="text-amber-400/50">retry #{job.retry_count}</span>}
                  </div>
                  {job.error_message && <p className="text-red-400/60 text-xs mt-1">{job.error_message}</p>}
                  {job.status === 'running' && (
                    <div className="mt-2 h-1.5 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-cyan-500/50 rounded-full transition-all" style={{ width: `${job.progress}%` }} />
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-1 flex-shrink-0">
                  {(job.status === 'pending' || job.status === 'running') && (
                    <button onClick={() => cancelJob(job.job_id)} className="text-white/20 hover:text-red-400 transition p-1" title="Cancel">
                      <XCircle className="w-4 h-4" />
                    </button>
                  )}
                  {job.status === 'failed' && (
                    <button onClick={() => retryJob(job.job_id)} className="text-white/20 hover:text-cyan-400 transition p-1" title="Retry">
                      <RotateCcw className="w-4 h-4" />
                    </button>
                  )}
                  {['completed', 'failed', 'cancelled'].includes(job.status) && (
                    <button onClick={() => deleteJob(job.job_id)} className="text-white/20 hover:text-red-400 transition p-1" title="Delete">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
